import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { workTimeApi, type SaveWorkTimeItem } from '../../api/workTime';
import { workerContractsApi } from '../../api/workerContracts';
import { useIsMirror } from '../../hooks/useIsMirror';
import { useReportScreen } from '../../components/ScreenContext';
import LoadingSpinner from '../../components/LoadingSpinner';

/**
 * P1c — Darbo laiko apskaita: one month, one row per active contract. The
 * classic accountant layout (simple table, not a calendar grid): per worker —
 * worked days/hours, sick and holiday day counts, special hours (overtime,
 * night, rest-holiday). Month save = upsert (idempotent). Mirror workspaces
 * are read-only (ADR-004).
 */
export default function WorkTimeSheetPage() {
  const isMirror = useIsMirror();
  const queryClient = useQueryClient();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const { data: sheet, isLoading, isError } = useQuery({
    queryKey: ['work-time', year, month],
    queryFn: () => workTimeApi.sheet(year, month),
  });
  const { data: contracts } = useQuery({
    queryKey: ['worker-contracts'],
    queryFn: workerContractsApi.list,
    staleTime: 5 * 60 * 1000,
  });

  const activeContracts = useMemo(
    () => (contracts ?? []).filter(c => c.isActive),
    [contracts],
  );

  // Editable rows: seed from the saved sheet, else from the active contracts.
  const [rows, setRows] = useState<Record<number, EditableRow> | null>(null);
  const seededKey = sheet ? `${sheet.year}-${sheet.month}-${sheet.exists}` : null;
  const [seededFor, setSeededFor] = useState<string | null>(null);

  const classes = sheet?.classes ?? [];
  const dayClasses = classes.filter(c => !c.isSpecialSchema);
  const specialClasses = classes.filter(c => c.isSpecialSchema);

  const ensureRows = useCallback((): Record<number, EditableRow> => {
    const key = seededKey ?? 'none';
    if (rows && seededFor === key) return rows;
    const seed: Record<number, EditableRow> = {};
    const savedByWorker = new Map((sheet?.items ?? []).map(i => [i.workerId, i]));
    for (const c of activeContracts) {
      const saved = savedByWorker.get(c.workerId);
      const sickClass = dayClasses.find(k => k.typeId === 8);
      const holidayClass = dayClasses.find(k => k.typeId === 9);
      seed[c.workerId] = {
        workerId: c.workerId,
        contractNumber: c.contractNumber,
        workedDays: saved ? saved.days.reduce((s, d) => s + d.countedDays, 0) : 0,
        workedHours: saved ? saved.days.reduce((s, d) => s + d.hours, 0) : 0,
        sickDays: saved
          ? saved.days
              .filter(d => sickClass && d.workTimeClassId === sickClass.id)
              .reduce((s, d) => s + d.countedDays, 0)
          : 0,
        holidayDays: saved
          ? saved.days
              .filter(d => holidayClass && d.workTimeClassId === holidayClass.id)
              .reduce((s, d) => s + d.countedDays, 0)
          : 0,
        special: Object.fromEntries(
          specialClasses.map(sc => [
            sc.id,
            saved?.specialHours.find(s => s.workTimeClassId === sc.id)?.totalHours ?? 0,
          ]),
        ),
      };
    }
    // Preserve unsaved edits when re-seeding the same key.
    if (rows) {
      for (const [id, old] of Object.entries(rows)) {
        if (dirty && seed[Number(id)]) seed[Number(id)] = old;
      }
    }
    setRows(seed);
    setSeededFor(key);
    return seed;
  }, [rows, seededFor, seededKey, sheet, activeContracts, dayClasses, specialClasses, dirty]);

  const currentRows = rows && seededFor === (seededKey ?? 'none') ? rows : ensureRows();

  const setRow = (workerId: number, patch: Partial<EditableRow>) => {
    setRows(prev => {
      if (!prev || !prev[workerId]) return prev;
      return { ...prev, [workerId]: { ...prev[workerId], ...patch } };
    });
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const monthStart = new Date(Date.UTC(year, month - 1, 1));
      const items: SaveWorkTimeItem[] = Object.values(currentRows ?? {}).map(r => {
        const sickClass = dayClasses.find(k => k.typeId === 8);
        const holidayClass = dayClasses.find(k => k.typeId === 9);
        const workClass = dayClasses.find(k => k.typeId === 1);
        const days: SaveWorkTimeItem['days'] = [];
        const day = (type: number, count: number) => {
          const cls = type === 8 ? sickClass : type === 9 ? holidayClass : workClass;
          if (!cls || count <= 0) return;
          for (let i = 0; i < Math.round(count); i++)
            days.push({ date: iso(monthStart), workTimeClassId: cls.id, countedDays: 1, hours: 0 });
        };
        // Represent counts as dated day marks (the engine reads day-schema
        // classes 8/9; ordinary worked time stays as totals).
        day(8, r.sickDays);
        day(9, r.holidayDays);
        return {
          workerId: r.workerId,
          contractNumber: r.contractNumber,
          days,
          specialHours: Object.entries(r.special).map(([classId, hours]) => ({
            workTimeClassId: Number(classId),
            totalHours: hours,
          })),
        };
      });
      await workTimeApi.save(year, month, items);
      setDirty(false);
      await queryClient.invalidateQueries({ queryKey: ['work-time', year, month] });
    } catch (e: any) {
      setError(e.message || 'Nepavyko išsaugoti');
    } finally {
      setSaving(false);
    }
  };

  useReportScreen(
    `Darbo laikas ${year}-${String(month).padStart(2, '0')}: ${Object.keys(currentRows ?? {}).length} darbuotojų`,
    { recordCount: Object.keys(currentRows ?? {}).length, filters: { metai: String(year), menuo: String(month) } },
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between py-2 border-b border-gray-100">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Darbo laiko apskaita</h1>
          <p className="text-sm text-gray-500">
            Mėnesio darbo laikas pagal sutartis — žiniaraščių skaičiavimo įvestis.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/payroll"
            className="px-4 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:border-gray-400"
          >
            ← Darbo užmokestis
          </Link>
          {!isMirror && (
            <button
              onClick={save}
              disabled={saving || !dirty}
              className="px-4 py-1.5 text-sm font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-40"
            >
              {saving ? '…' : dirty ? 'Išsaugoti' : '✓'}
            </button>
          )}
        </div>
      </div>

      <div className="filters" style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
        <label>
          Metai:{' '}
          <input
            type="number"
            value={year}
            min={2000}
            max={2100}
            onChange={e => setYear(Number(e.target.value))}
          />
        </label>
        <label>
          Mėnuo:{' '}
          <select value={month} onChange={e => setMonth(Number(e.target.value))}>
            {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </label>
        {sheet?.exists && !dirty && <span className="text-xs text-gray-400">Įrašyta</span>}
      </div>

      {isMirror && (
        <p className="text-sm text-gray-500">Ši darbo sritis tik skaityti — darbo laiką veskite Apskaita5 programoje.</p>
      )}
      {error && <p className="text-sm text-red-600">{error}</p>}

      {isError && <div className="error-message">Nepavyko įkelti tabelio — atnaujinkite puslapį.</div>}
      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>Darbuotojas</th>
                <th style={{ textAlign: 'right' }}>Dienos</th>
                <th style={{ textAlign: 'right' }}>Valandos</th>
                <th style={{ textAlign: 'right' }}>Nedarbingumo d.</th>
                <th style={{ textAlign: 'right' }}>Atostogų d.</th>
                {specialClasses.map(sc => (
                  <th key={sc.id} style={{ textAlign: 'right' }}>
                    {sc.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Object.values(currentRows ?? {}).map(r => (
                <tr key={r.workerId}>
                  <td>
                    {activeContracts.find(c => c.workerId === r.workerId)?.workerName ?? r.workerId}
                  </td>
                  <NumCell value={r.workedDays} disabled={isMirror} onChange={v => setRow(r.workerId, { workedDays: v })} />
                  <NumCell value={r.workedHours} disabled={isMirror} onChange={v => setRow(r.workerId, { workedHours: v })} />
                  <NumCell value={r.sickDays} disabled={isMirror} onChange={v => setRow(r.workerId, { sickDays: v })} />
                  <NumCell value={r.holidayDays} disabled={isMirror} onChange={v => setRow(r.workerId, { holidayDays: v })} />
                  {specialClasses.map(sc => (
                    <NumCell
                      key={sc.id}
                      value={r.special[sc.id] ?? 0}
                      disabled={isMirror}
                      onChange={v => setRow(r.workerId, { special: { ...r.special, [sc.id]: v } })}
                    />
                  ))}
                </tr>
              ))}
              {Object.keys(currentRows ?? {}).length === 0 && (
                <tr>
                  <td colSpan={5 + specialClasses.length}>
                    Aktyvių sutarčių nėra — pradėkite nuo darbuotojų sutarčių įvedimo.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

interface EditableRow {
  workerId: number;
  contractNumber: number;
  workedDays: number;
  workedHours: number;
  sickDays: number;
  holidayDays: number;
  special: Record<number, number>;
}

function NumCell({
  value,
  onChange,
  disabled,
}: {
  value: number;
  onChange: (v: number) => void;
  disabled: boolean;
}) {
  return (
    <td style={{ textAlign: 'right' }}>
      <input
        type="number"
        step="0.5"
        value={value}
        disabled={disabled}
        onChange={e => onChange(Number(e.target.value))}
        style={{ width: '4.5rem', textAlign: 'right' }}
      />
    </td>
  );
}

function iso(d: Date): string {
  return d.toISOString().slice(0, 10);
}
